import React, { useState, useEffect, useRef } from 'react';
import './AddressInput.css';

/**
 * AddressInput Component
 * 
 * A reusable address input component with OpenStreetMap/Nominatim autocomplete.
 * Provides address suggestions and extracts coordinates on selection.
 * 
 * @param {string} value - Current address value
 * @param {function} onChange - Callback when address text changes
 * @param {function} onLocationSelect - Callback when address is selected (receives { address, latitude, longitude, street, city, postcode, country })
 * @param {string} placeholder - Placeholder text
 * @param {string} error - Error message to display
 * @param {boolean} required - Whether field is required
 * @param {string} id - Input ID
 * @param {string} name - Input name
 * @param {string} className - Additional CSS classes
 */
const AddressInput = ({
  value = '',
  onChange,
  onLocationSelect,
  placeholder = 'Enter your address',
  error,
  required = false,
  id,
  name,
  className = '',
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState(value);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Sync external value with internal search query
  useEffect(() => {
    if (value !== searchQuery && !showSuggestions) {
      setSearchQuery(value);
    }
  }, [value]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const searchAddresses = async (query) => {
    if (!query || query.trim().length < 3) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    setIsLoading(true);

    try {
      // Nominatim API endpoint - restricted to UK (gb)
      // Note: Nominatim has a rate limit of 1 request per second
      const encodedQuery = encodeURIComponent(query);
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodedQuery}&countrycodes=gb&limit=5&addressdetails=1`;

      const response = await fetch(url, {
        signal: abortControllerRef.current.signal,
        headers: {
          // Nominatim requires a proper User-Agent with contact information
          // Format: AppName/Version (contact information)
          'User-Agent': 'SageLocum/1.0 (https://sagelocum.com)',
          'Accept': 'application/json',
          'Accept-Language': 'en-GB,en;q=0.9',
        },
      });

      if (!response.ok) {
        if (response.status === 403) {
          console.warn('Nominatim API returned 403 Forbidden. This may be due to rate limiting or User-Agent requirements.');
          // Still allow manual address entry
          setSuggestions([]);
          return;
        }
        throw new Error(`Failed to fetch addresses: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setSuggestions(data || []);
    } catch (error) {
      if (error.name === 'AbortError') {
        // Request was cancelled, ignore
        return;
      }
      console.error('Error fetching addresses:', error);
      setSuggestions([]);
      // Don't show error to user - they can still type address manually
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    setShowSuggestions(true);

    // Clear previous debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce API calls (Nominatim rate limit: 1 request/second)
    debounceTimerRef.current = setTimeout(() => {
      searchAddresses(newValue);
    }, 500); // 500ms debounce to respect rate limits

    // Call onChange if provided
    if (onChange) {
      onChange(e);
    }
  };

  const handleSelect = (suggestion) => {
    const address = suggestion.display_name || '';
    const latitude = parseFloat(suggestion.lat);
    const longitude = parseFloat(suggestion.lon);

    // Extract address components from Nominatim response
    const addressDetails = suggestion.address || {};
    
    const locationData = {
      address: address,
      latitude: latitude,
      longitude: longitude,
      street: [
        addressDetails.road,
        addressDetails.house_number,
      ].filter(Boolean).join(' ').trim() || '',
      city: addressDetails.city || 
            addressDetails.town || 
            addressDetails.village || 
            addressDetails.municipality || '',
      postcode: addressDetails.postcode || '',
      country: addressDetails.country || 'United Kingdom',
    };

    setSearchQuery(address);
    setShowSuggestions(false);
    setSuggestions([]);

    // Call onLocationSelect callback
    if (onLocationSelect) {
      onLocationSelect(locationData);
    }

    // Trigger onChange with the selected address
    if (onChange) {
      const syntheticEvent = {
        target: {
          name: name || 'address',
          value: address,
        },
      };
      onChange(syntheticEvent);
    }
  };

  const handleInputFocus = () => {
    if (suggestions.length > 0 || searchQuery.length >= 3) {
      setShowSuggestions(true);
      if (searchQuery.length >= 3) {
        searchAddresses(searchQuery);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  return (
    <div className={`address-input ${className}`} ref={wrapperRef}>
      <div className="address-input__wrapper">
        <input
          ref={inputRef}
          type="text"
          id={id}
          name={name || 'address'}
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          required={required}
          className={`address-input__field ${error ? 'address-input__field--error' : ''}`}
          autoComplete="off"
        />
        {isLoading && (
          <div className="address-input__loading">
            <span className="address-input__spinner"></span>
          </div>
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <ul className="address-input__suggestions">
          {suggestions.map((suggestion, index) => (
            <li
              key={`${suggestion.place_id || index}-${suggestion.osm_id || index}`}
              className="address-input__suggestion"
              onClick={() => handleSelect(suggestion)}
              onMouseDown={(e) => e.preventDefault()} // Prevent input blur
            >
              <div className="address-input__suggestion-main">
                {suggestion.display_name}
              </div>
            </li>
          ))}
        </ul>
      )}

      {showSuggestions && searchQuery.length >= 3 && !isLoading && suggestions.length === 0 && (
        <ul className="address-input__suggestions">
          <li className="address-input__suggestion address-input__suggestion--no-results">
            No addresses found. You can still type your address manually.
          </li>
        </ul>
      )}

      {error && <span className="address-input__error">{error}</span>}
    </div>
  );
};

export default AddressInput;
